// `Breadcrumbs` + `PageHeader` (design-system spec §3.2). Every content screen
// opens with ONE page-head row: breadcrumbs left (the current segment IS the
// screen's h1-equivalent — nothing in the office renders above --text-lg), and
// the page's tools right. PageHeader keeps the API contract
// (`title`, `meta`, `actions`, `backTo`) and renders this row.

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

export interface Crumb {
  label: ReactNode;
  /** Route path; the current (last) crumb has none. */
  to?: string | undefined;
}

export function Breadcrumbs({
  items,
  ...rest
}: {
  items: readonly Crumb[];
  "data-testid"?: string;
}) {
  return (
    <nav className="ds-crumbs" aria-label="Breadcrumb" {...rest}>
      {items.map((item, i) => {
        const last = i === items.length - 1;
        return (
          <span key={i} className="ds-crumb" style={{ display: "contents" }}>
            {item.to !== undefined && !last ? (
              <Link to={item.to}>{item.label}</Link>
            ) : (
              <span
                className={last ? "ds-crumb-cur" : undefined}
                aria-current={last ? "page" : undefined}
                /* THE CURRENT SEGMENT IS THE SCREEN'S ONE h1-EQUIVALENT (§3.2:
                 * "there is no separate hero title"). Saying that in prose and
                 * not in the accessibility tree left every migrated screen with
                 * NO level-1 heading at all — a screen reader's "jump to the
                 * heading" lands nowhere, and the certified M02 responsive net,
                 * which asks for the level-1 heading by name, could no longer
                 * find the products screen it embeds. Same box, same 12.5 px
                 * type, correct semantics. */
                role={last ? "heading" : undefined}
                aria-level={last ? 1 : undefined}
              >
                {item.label}
              </span>
            )}
            {!last ? (
              <span className="ds-crumb-sep" aria-hidden="true">
                /
              </span>
            ) : null}
          </span>
        );
      })}
    </nav>
  );
}

export interface PageHeaderProps {
  /** The current segment — the screen's one h1-equivalent. */
  title: ReactNode;
  /** Parent crumbs before the title. */
  crumbs?: readonly Crumb[];
  /** Right-side meta text ("Updated 14:32"). */
  meta?: ReactNode;
  /** Right-side tools (segmented range, icon actions). */
  actions?: ReactNode;
  /** Optional single parent shortcut when no crumb trail is given. */
  backTo?: { label: ReactNode; to: string } | undefined;
  /**
   * Rev-7 (D-425): the screen's one lede, a single line of 12 words or
   * fewer under the head row. Anything longer belongs in a `HelpTip` or the
   * guide, and `tools/ci/copy-budget.ts` holds the catalogue to that.
   */
  lede?: ReactNode;
  "data-testid"?: string;
}

export function PageHeader({
  title,
  crumbs,
  meta,
  actions,
  backTo,
  lede,
  ...rest
}: PageHeaderProps) {
  const trail: Crumb[] = [
    ...(crumbs ?? (backTo !== undefined ? [{ label: backTo.label, to: backTo.to }] : [])),
    { label: title },
  ];
  const head = (
    <div className="ds-pagehead" {...(lede === undefined ? rest : {})}>
      <Breadcrumbs items={trail} />
      {actions !== undefined || meta !== undefined ? (
        <div className="ds-pagehead-right">
          {actions}
          {meta !== undefined ? <span className="num">{meta}</span> : null}
        </div>
      ) : null}
    </div>
  );
  // Without a lede the markup is exactly what it was before rev-7, so no
  // screen that passes none moves.
  if (lede === undefined) return head;
  return (
    <div className="ds-pagehead-wrap" {...rest}>
      {head}
      <p className="ds-lede">{lede}</p>
    </div>
  );
}
